import { faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import { AUTHORITIES, DEFAULT_VIRTUAL_FENCE_COLOR } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';
import { getEntities as getEntitiesPlaces } from 'app/entities/place/place.reducer';
import { getEntities as getEntitiesVirtualFenceCircle } from 'app/entities/virtual-fence-circle/virtual-fence-circle.reducer';
import { getEntitiesForMap as getEntitiesVirtualFencePolygon } from 'app/entities/virtual-fence-polygon/virtual-fence-polygon.reducer';
import { countMaintenanceControl } from 'app/modules/dashboards/dashboard-company/dashboard-company.reducer';
import { UserMapSetupCollapse } from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-journey-tab/UserMapSetupCollapse';
import {
  IdStorageTypes,
  ValuesStorageTypes,
  getStorageOptions,
  setStorageOptions,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-journey-tab/storageOptions';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import { NavDropdown } from 'app/shared/layout/menus/menu-components';
import { IDashboardCompanyImplementsInfoDetails } from 'app/shared/model/dashboard-company-implements-info-details.model';
import { IDashboardCompanyImplementsInfo } from 'app/shared/model/dashboard-company-implements-info.model';
import { TratativaStatus } from './company-vehicle-list';
import { convertDateTimeToPtBRWithYear } from 'app/shared/util/date-utils';
import { mechanicalWorkshopIcon, pointsOfInterestClusterStyle } from 'app/shared/util/iconStylesAndClusters';
import { Loading } from 'app/shared/util/loading';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoogleMap, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';
import InfoWindow from 'react-google-maps/lib/components/InfoWindow';
import Circle from 'react-google-maps/lib/components/Circle';
import Polygon from 'react-google-maps/lib/components/Polygon';
import TrafficLayer from 'react-google-maps/lib/components/TrafficLayer';
import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';
import { translate } from 'react-jhipster';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { compose, withProps } from 'recompose';
import useSupercluster from 'use-supercluster';
import type Supercluster from 'supercluster';
import { getDashboardCompanyImplementsInfoDetails } from '../../dashboard-company.reducer';
import { STATUS_COLORS } from './company-vehicle-list/constants';
import './CompanyTruckImplementsMap.scss';

// ─── Supercluster integration ───────────────────────────────────────────────
// Each truck point is encoded as a GeoJSON feature with the metadata we need to
// decide marker color, aggregate status counts into the cluster, and navigate when the user clicks it.
type StatusCategory = 'ALERT' | 'AGUARDANDO' | 'OFF' | 'OUTDATED' | 'ON';
type ClusterRollup = {
  alert: number;
  aguardando: number;
  off: number;
  outdated: number;
  on: number;
};
type TruckPointProperties = {
  cluster: false;
  vehicleId: number;
  licensePlate: string;
  color: string;
  status: StatusCategory;
};
type ClusterProperties = {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: number | string;
} & Partial<ClusterRollup>;

// Resolves the dominant cluster kind via the same priority used by individual markers:

const pickClusterKind = (r: Partial<ClusterRollup>): 'alert' | 'aguardando' | 'outdated' | 'off' | 'on' => {
  if ((r.alert ?? 0) > 0) return 'alert';
  if ((r.aguardando ?? 0) > 0) return 'aguardando';
  if ((r.outdated ?? 0) > 0) return 'outdated';
  if ((r.off ?? 0) > 0) return 'off';
  return 'on';
};

// Type guard: narrows a properties union to the truck-point variant.
const isTruckPoint = (p: TruckPointProperties | ClusterProperties): p is TruckPointProperties => p.cluster === false;

const isValidLatLng = (item?: { lat?: number | null; lng?: number | null }) => {
  const lat = Number(item?.lat);
  const lng = Number(item?.lng);

  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const CompanyTruckImplementsMap = ({
  props,
  tratativaOverrides = {},
  focusedAsset = null,
  focusedAssetTick = 0,
  vehicles,
}: {
  props: any;
  tratativaOverrides?: Record<number, TratativaStatus>;
  focusedAsset?: IDashboardCompanyImplementsInfo | null;
  focusedAssetTick?: number;
  vehicles: IDashboardCompanyImplementsInfo[];
}) => {
  const initialPosition = [
    { lat: 4.326383914161625, lng: -55.787664895705404 },
    { lat: -31.40830039764787, lng: -52.71149302062307 },
  ];
  const dispatch = useDispatch();
  const mapRef = useRef<any>(null);
  const [center] = useState<{ lat: number; lng: number }>({ lat: -14.499173, lng: -51.445122 });
  const [zoom] = useState<number>(4);

  // ─── Viewport state — drives Supercluster's `bounds` and `zoom` inputs ─────
  // We capture map state on `onIdle` (camera at rest) plus a small debounce so a single
  // user gesture doesn't trigger many recomputes.
  const [viewportBounds, setViewportBounds] = useState<[number, number, number, number] | null>(null);
  const [viewportZoom, setViewportZoom] = useState<number>(4);
  const idleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activeMarkerId, setActiveMarkerId] = useState<number | null>(null);
  const [onActiveMarker, setOnActiveMarker] = useState<boolean>(false);
  const [isHiddenCircle, setIsHiddenCircle] = useState<boolean>(false);
  const [isHiddenPlaces, setIsHiddenPlaces] = useState<boolean>(false);
  const [isHiddenTruckImplements, setIsHiddenTruckImplements] = useState<boolean>(true);
  const [truckImplementId] = useState<number | null>(null);
  const [detailsTimedOut, setDetailsTimedOut] = useState<boolean>(false);
  const detailsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [checkedRoutes, setCheckedRoutes] = useState<boolean>(getStorageOptions(IdStorageTypes.ROUTES) ?? true);
  const [mapType, setMapType] = useState<ValuesStorageTypes>(getStorageOptions(IdStorageTypes.MAP_TYPE) ?? ValuesStorageTypes.MAP);
  const [checkedTraffic, setCheckedTraffic] = useState<boolean>(getStorageOptions(IdStorageTypes.TRAFFIC) ?? false);
  const [companyImplementsInfoDetails, setCompanyImplementsInfoDetails] = useState<IDashboardCompanyImplementsInfoDetails | null>(null);
  const placeList = useAppSelector(state => state.place.entities);
  const virtualFenceCircleList = useAppSelector(state => state.virtualFenceCircle.entities);
  const virtualFencePolygonList = useAppSelector(state => state.virtualFencePolygon.entities);
  const canSeeVirtualFences = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.MANUFACTURER_ADMIN, AUTHORITIES.TRANSPORTER_ADMIN])
  );
  const loadingCompanyInfo = useAppSelector(state => state.dashboardCompany.loadingCompanyInfo);
  const dashboardCompanyImplementsInfoDetails = useAppSelector(state => state.dashboardCompany.dashboardCompanyImplementsInfoDetails);
  const hasInitialFitRef = useRef(false);

  // Used by the InfoWindow to read the latest data for the active vehicle.
  const implementsInfoById = useMemo(() => {
    const m: Record<number, IDashboardCompanyImplementsInfo> = {};
    vehicles.forEach(item => {
      if (item.id != null) m[Number(item.id)] = item;
    });
    return m;
  }, [vehicles]);

  // Pre-compute marker color + status category per vehicle id once per data change.
  const markerMetaById = useMemo(() => {
    const m = new Map<number, { color: string; status: StatusCategory }>();
    for (const item of vehicles) {
      if (item.id == null) continue;
      const id = Number(item.id);
      const hasAlert = !!((item.alert && item.alert !== 'Ok') || (item.tireAlert && item.tireAlert !== 'Ok' && item.tireAlert !== 'SEM_SENSOR'));
      const hasUnsafeFence = item.virtualFenceRiskClassification === 'UNSAFE';
      const offRoute = item.routeStatus === 'OFF_ROUTE';

      const localTrat = tratativaOverrides[id];

      let status: StatusCategory;
      let color: string;
      if (hasAlert && localTrat === 'AGUARDANDO_CONFIRMACAO') {
        status = 'AGUARDANDO';
        color = STATUS_COLORS.warning;
      } else if (hasAlert || hasUnsafeFence || offRoute) {
        status = 'ALERT';
        color = STATUS_COLORS.alertBar;
      } else if (!item.isPowerOn) {
        status = 'OFF';
        color = STATUS_COLORS.muted;
      } else if (item.outdated) {
        status = 'OUTDATED';
        color = STATUS_COLORS.faded;
      } else {
        status = 'ON';
        color = STATUS_COLORS.successAlt;
      }

      m.set(id, { color, status });
    }
    return m;
  }, [vehicles, tratativaOverrides]);

  const [availableMapHeight, setAvailableMapHeight] = useState<number | null>(null);
  const getCompanyImplementsInfoDetails = (id: number) => {
    setCompanyImplementsInfoDetails(null);
    setDetailsTimedOut(false);
    if (detailsTimeoutRef.current) clearTimeout(detailsTimeoutRef.current);
    detailsTimeoutRef.current = setTimeout(() => setDetailsTimedOut(true), 10000);
    dispatch(getDashboardCompanyImplementsInfoDetails(id));
  };

  const verifyOnMarker = (id: number) => {
    setActiveMarkerId(id);
    setOnActiveMarker(prev => !prev);
  };

  // Captures map bounds/zoom when the camera stops moving (onIdle), with an 80ms debounce.
  const handleMapIdle = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (idleDebounceRef.current) clearTimeout(idleDebounceRef.current);
    idleDebounceRef.current = setTimeout(() => {
      const b = map.getBounds?.();
      const z = map.getZoom?.();
      if (b) {
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        // Supercluster expects [west, south, east, north] in lng/lat.
        setViewportBounds([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
      }
      if (typeof z === 'number') setViewportZoom(z);
    }, 80);
  }, []);

  useEffect(
    () => () => {
      if (idleDebounceRef.current) clearTimeout(idleDebounceRef.current);
    },
    []
  );

  function renderRedirect() {
    return <Redirect to={`/dashboards/truck-implement/${truckImplementId}?tab=1`} />;
  }

  const renderButtonRoutes = useCallback(() => {
    const getDivButtonTruckImplements = document.querySelector('.div-button-truck-implements');
    const getDivButtonPlaces = document.querySelector('.div-button-places');
    const getDivButtonCircle = document.querySelector('.div-button-circle');
    const getDivButtonOptions = document.querySelector('.div-button-options');

    const divMap = document.querySelector('.gm-style');

    if (!getDivButtonTruckImplements) {
      const divButtonTruckImplements = document.createElement('div');
      divButtonTruckImplements.classList.add('div-button-truck-implements');
      divMap?.append(divButtonTruckImplements);
    }

    if (!getDivButtonOptions) {
      const divButtonOptions = document.createElement('div');
      divButtonOptions.classList.add('div-button-options');
      divMap?.append(divButtonOptions);
    }

    if (!canSeeVirtualFences) {
      getDivButtonCircle?.remove();
    }

    if (canSeeVirtualFences && !getDivButtonCircle) {
      const divButtonCircle = document.createElement('div');
      divButtonCircle.classList.add('div-button-circle');
      divMap?.append(divButtonCircle);
    }

    if (!getDivButtonPlaces) {
      // const divButtonPlaces = document.createElement('div');
      // divButtonPlaces.classList.add('div-button-places');
      // divMap?.append(divButtonPlaces);
    }

    if (getDivButtonTruckImplements) {
      ReactDOM.render(
        <Box position={'absolute'} right={10} bottom={'355px'}>
          <NavDropdown id="drop-sub-options" direction="up" imageSrc="content/images/map-icons/icon_pin_truck_black.png">
            <div className="container-options">
              <FormControlLabel
                control={<Checkbox />}
                label={translate('fleetSenseApp.truckImplement.home.title')}
                onChange={(e, checked) => setIsHiddenTruckImplements(checked)}
                checked={isHiddenTruckImplements}
              />
            </div>
          </NavDropdown>
        </Box>,
        getDivButtonTruckImplements
      );
    }

    if (getDivButtonPlaces) {
      ReactDOM.render(
        <Box position={'absolute'} right={10} bottom={'285px'}>
          <NavDropdown id="drop-sub-options" icon={faThumbtack} direction="up">
            <div className="container-options">
              <FormControlLabel
                control={<Checkbox />}
                label={translate('fleetSenseApp.PlaceTypeEnum.REPAIR_SHOP')}
                onChange={(e, checked) => setIsHiddenPlaces(checked)}
              />
            </div>
          </NavDropdown>
        </Box>,
        getDivButtonPlaces
      );
    }

    if (canSeeVirtualFences && getDivButtonCircle) {
      ReactDOM.render(
        <Box position={'absolute'} right={10} bottom={'285px'}>
          <NavDropdown id="drop-sub-options" icon="map" direction="up">
            <div className="container-options">
              <FormControlLabel
                control={<Checkbox checked={isHiddenCircle} />}
                label={translate('global.menu.entities.virtualFenceCircle')}
                onChange={(e, checked) => setIsHiddenCircle(checked)}
              />
            </div>
          </NavDropdown>
        </Box>,
        getDivButtonCircle
      );
    }

    if (getDivButtonOptions) {
      ReactDOM.render(
        <UserMapSetupCollapse
          checkedRoutes={checkedRoutes}
          setCheckedRoutes={setCheckedRoutes}
          checkedTraffic={checkedTraffic}
          setCheckedTraffic={setCheckedTraffic}
          isCompany
        />,
        getDivButtonOptions
      );
    }
  }, [canSeeVirtualFences, checkedRoutes, checkedTraffic, isHiddenTruckImplements, isHiddenCircle]);

  // ─── Cached truck icon factory (single asset) ────────────────────────────
  const truckIconCache = useRef<Map<string, google.maps.Icon>>(new Map());

  const makeTruckIcon = useCallback((color: string, plate: string): google.maps.Icon => {
    const cacheKey = `${color}|${plate}`;
    const cached = truckIconCache.current.get(cacheKey);
    if (cached) return cached;

    const hex = color.startsWith('#') && color.length === 9 ? color.slice(0, 7) : color;

    const outerR = 22;
    const innerR = 14;
    const pad = 5;
    const circleW = (outerR + pad) * 2;

    const pillFontSize = 11;
    const pillH = 18;
    const pillW = Math.max(circleW, Math.ceil(plate.length * pillFontSize * 0.63) + 16);

    const totalW = Math.max(circleW, pillW + 4);
    const cx = totalW / 2;
    const cy = outerR + pad;

    const pillY = cy + outerR + 4;
    const pillX = (totalW - pillW) / 2;
    const totalH = pillY + pillH + 4;

    const iconSize = innerR * 1.6;
    const iconScale = iconSize / 24;
    const iconX = cx - iconSize / 2;
    const iconY = cy - iconSize / 2;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">
      <circle cx="${cx}" cy="${cy}" r="${outerR}" fill="${hex}1A" stroke="${hex}" stroke-width="2"/>
      <circle cx="${cx}" cy="${cy}" r="${innerR}" fill="white" stroke="${hex}" stroke-width="1.5"/>
      <g transform="translate(${iconX},${iconY}) scale(${iconScale})">
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="${hex}"/>
      </g>
      <rect x="${pillX}" y="${pillY}" width="${pillW}" height="${pillH}" rx="${pillH / 2}"
        fill="white" stroke="${hex}" stroke-width="1.5"/>
      <text x="${cx}" y="${pillY + pillH / 2 + 1}" text-anchor="middle" dominant-baseline="middle"
        font-family="Arial,sans-serif" font-size="${pillFontSize}" font-weight="700" fill="#111">${plate}</text>
    </svg>`;

    const icon: google.maps.Icon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(totalW, totalH),
      anchor: new google.maps.Point(cx, cy),
    };

    truckIconCache.current.set(cacheKey, icon);
    return icon;
  }, []);

  // ─── Cluster icon factory — sized by point count, colored by dominant status ──
  const clusterIconCache = useRef<Map<string, google.maps.Icon>>(new Map());

  const makeClusterIcon = useCallback((count: number, kind: 'alert' | 'aguardando' | 'outdated' | 'off' | 'on'): google.maps.Icon => {
    const sizeBucket = count < 10 ? 0 : count < 50 ? 1 : count < 200 ? 2 : count < 1000 ? 3 : 4;
    const cacheKey = `${sizeBucket}|${kind}|${count}`;
    const cached = clusterIconCache.current.get(cacheKey);
    if (cached) return cached;

    const sizes = [40, 48, 56, 64, 72];
    const r = sizes[sizeBucket] / 2;
    const total = sizes[sizeBucket];

    let ring: string;
    let fill: string;
    let text: string;
    switch (kind) {
      case 'alert':
        ring = STATUS_COLORS.alertBar;
        fill = '#fff5f5';
        text = STATUS_COLORS.alertBar;
        break;
      case 'aguardando':
        ring = STATUS_COLORS.warning;
        fill = '#fff8e6';
        text = '#a16400';
        break;
      case 'outdated':
        ring = STATUS_COLORS.faded;
        fill = '#eef0fb';
        text = '#3a45a8';
        break;
      case 'off':
        ring = STATUS_COLORS.muted;
        fill = '#f3f3f3';
        text = '#444';
        break;
      case 'on':
      default:
        ring = STATUS_COLORS.successAlt;
        fill = '#eaf7ea';
        text = STATUS_COLORS.successAlt;
        break;
    }

    const fontSize = total < 50 ? 13 : 15;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${total}" height="${total}">
      <circle cx="${r}" cy="${r}" r="${r - 4}" fill="${fill}" stroke="${ring}" stroke-width="3" opacity="0.92"/>
      <circle cx="${r}" cy="${r}" r="${r - 9}" fill="${ring}" opacity="0.18"/>
      <text x="${r}" y="${r + 1}" text-anchor="middle" dominant-baseline="middle"
        font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="700" fill="${text}">${count}</text>
    </svg>`;

    const icon: google.maps.Icon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(total, total),
      anchor: new google.maps.Point(r, r),
    };
    clusterIconCache.current.set(cacheKey, icon);
    return icon;
  }, []);

  // ─── Supercluster input: GeoJSON points with status metadata for cluster rollup ──
  const points = useMemo<Array<Supercluster.PointFeature<TruckPointProperties>>>(() => {
    const out: Array<Supercluster.PointFeature<TruckPointProperties>> = [];
    for (const item of vehicles) {
      if (item.id == null || item.lat == null || item.lng == null) continue;
      const id = Number(item.id);
      const meta = markerMetaById.get(id) ?? { color: STATUS_COLORS.successAlt, status: 'ON' as StatusCategory };
      out.push({
        type: 'Feature',
        properties: {
          cluster: false,
          vehicleId: id,
          licensePlate: item.licensePlate ?? '',
          color: meta.color,
          status: meta.status,
        },
        geometry: { type: 'Point', coordinates: [item.lng, item.lat] },
      });
    }
    return out;
  }, [vehicles, markerMetaById]);

  // Roll up status counts into clusters to determine dominant color via the same priority as individual markers.
  const superclusterOptions = useMemo(
    () => ({
      radius: 60,
      maxZoom: 16,
      minPoints: 3,
      map(p: TruckPointProperties): ClusterRollup {
        return {
          alert: p.status === 'ALERT' ? 1 : 0,
          aguardando: p.status === 'AGUARDANDO' ? 1 : 0,
          off: p.status === 'OFF' ? 1 : 0,
          outdated: p.status === 'OUTDATED' ? 1 : 0,
          on: p.status === 'ON' ? 1 : 0,
        };
      },
      reduce(acc: ClusterRollup, p: Readonly<ClusterRollup>) {
        acc.alert += p.alert;
        acc.aguardando += p.aguardando;
        acc.off += p.off;
        acc.outdated += p.outdated;
        acc.on += p.on;
      },
    }),
    []
  );

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: viewportBounds ?? undefined,
    zoom: viewportZoom,
    options: superclusterOptions,
  });

  // Close the InfoWindow when the active marker gets absorbed into a cluster.
  useEffect(() => {
    if (!onActiveMarker || activeMarkerId == null || !clusters.length) return;
    const stillVisible = clusters.some(c => {
      const p = c.properties as TruckPointProperties | ClusterProperties;
      return isTruckPoint(p) && p.vehicleId === activeMarkerId;
    });
    if (!stillVisible) {
      setOnActiveMarker(false);
      setActiveMarkerId(null);
      setCompanyImplementsInfoDetails(null);
    }
  }, [clusters, activeMarkerId, onActiveMarker]);

  const handleClusterClick = useCallback(
    (clusterId: number, lng: number, lat: number) => {
      if (!supercluster) return;
      const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(clusterId), 18);
      mapRef.current?.panTo({ lat, lng });
      mapRef.current?.setZoom(expansionZoom);
    },
    [supercluster]
  );

  const renderTruckImplementMarkers = useCallback(() => {
    if (!clusters.length) return null;

    return (
      <>
        {clusters.map(c => {
          const [lng, lat] = c.geometry.coordinates;
          const cp = c.properties as TruckPointProperties | ClusterProperties;
          if (cp.cluster) {
            const id = `c-${cp.cluster_id}`;
            const kind = pickClusterKind(cp);
            return (
              <Marker
                key={id}
                position={{ lat, lng }}
                icon={makeClusterIcon(cp.point_count, kind)}
                onClick={() => handleClusterClick(cp.cluster_id, lng, lat)}
              />
            );
          }

          if (!isTruckPoint(cp)) return null;
          const tp = cp;
          const latest = implementsInfoById[tp.vehicleId];
          // Fall back to cluster point properties if vehicle data is unavailable.
          const item =
            latest ??
            ({
              id: tp.vehicleId,
              licensePlate: tp.licensePlate,
              lat,
              lng,
            } as IDashboardCompanyImplementsInfo);

          return (
            <Marker
              key={`t-${tp.vehicleId}`}
              position={{ lat, lng }}
              icon={makeTruckIcon(tp.color, tp.licensePlate)}
              onClick={() => {
                verifyOnMarker(tp.vehicleId);
                if (!onActiveMarker) {
                  getCompanyImplementsInfoDetails(tp.vehicleId);
                } else {
                  setCompanyImplementsInfoDetails(null);
                }
              }}
            >
              {onActiveMarker && activeMarkerId === tp.vehicleId && (
                <InfoWindow onCloseClick={() => verifyOnMarker(tp.vehicleId)}>
                  {companyImplementsInfoDetails ? (
                    <div
                      style={{
                        width: 'min(300px, 80vw)',
                        fontFamily: 'sans-serif',
                        fontSize: 13,
                        padding: '14px 16px',
                        boxSizing: 'border-box',
                        maxHeight: availableMapHeight != null ? Math.max(120, availableMapHeight / 2 - 50) : undefined,
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          marginBottom: 10,
                          borderBottom: '1px solid #e0e0e0',
                          paddingBottom: 6,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {companyImplementsInfoDetails.licensePlate}
                        {item.companyName ? <span style={{ fontWeight: 400, color: '#666', marginLeft: 6 }}>· {item.companyName}</span> : null}
                      </div>
                      {[
                        { label: 'Motorista', value: companyImplementsInfoDetails.fullName || '—' },
                        { label: 'Status', value: companyImplementsInfoDetails.activated ? 'Ligado' : 'Desligado' },
                        { label: 'Última posição', value: companyImplementsInfoDetails.address || '—' },
                        { label: 'Data', value: convertDateTimeToPtBRWithYear(companyImplementsInfoDetails.date) || '—' },
                      ].map(row => (
                        <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, marginBottom: 5 }}>
                          <span style={{ color: '#888', whiteSpace: 'nowrap' }}>{row.label}</span>
                          <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>{row.value}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 12 }}>
                        <div
                          style={{ color: '#1976d2', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                          onClick={() => props.history.push(`/dashboards/truck-implement/${tp.vehicleId}`)}
                        >
                          <FontAwesomeIcon icon="eye" />
                          Abrir detalhes
                        </div>
                      </div>
                    </div>
                  ) : detailsTimedOut ? (
                    <div style={{ width: 'min(220px, 80vw)', padding: '16px', textAlign: 'center', fontFamily: 'sans-serif', fontSize: 13, color: '#888' }}>
                      Não foi possível carregar os detalhes.
                    </div>
                  ) : (
                    <Loading noTheme />
                  )}
                </InfoWindow>
              )}
            </Marker>
          );
        })}
      </>
    );
  }, [
    clusters,
    activeMarkerId,
    onActiveMarker,
    companyImplementsInfoDetails,
    implementsInfoById,
    availableMapHeight,
    detailsTimedOut,
    makeTruckIcon,
    makeClusterIcon,
    handleClusterClick,
  ]);

  const renderVirtualFenceCircle = () => {
    return (
      virtualFenceCircleList?.length &&
      virtualFenceCircleList.map(virtualFenceCircle => (
        <Circle
          key={virtualFenceCircle.id}
          center={{ lat: virtualFenceCircle.latitude ?? 0, lng: virtualFenceCircle.longitude ?? 0 }}
          radius={virtualFenceCircle.radius ?? 0}
          options={{
            fillColor: virtualFenceCircle.color,
            fillOpacity: 0.5,
            zIndex: 1,
            strokeWeight: 0.3,
            strokeColor: virtualFenceCircle.color,
          }}
        />
      ))
    );
  };

  const renderVirtualFencePolygons = () => {
    return (
      virtualFencePolygonList?.length &&
      virtualFencePolygonList.map(polygon => {
        const path = (polygon.points ?? [])
          .filter(p => p.latitude != null && p.longitude != null)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map(p => ({ lat: p.latitude as number, lng: p.longitude as number }));

        if (path.length < 3) return null;

        return (
          <Polygon
            key={polygon.id}
            path={path}
            options={{
              fillColor: polygon.color ?? DEFAULT_VIRTUAL_FENCE_COLOR,
              fillOpacity: 0.35,
              strokeColor: polygon.color ?? DEFAULT_VIRTUAL_FENCE_COLOR,
              strokeOpacity: 0.9,
              strokeWeight: 2,
              zIndex: 1,
            }}
          />
        );
      })
    );
  };

  const renderPointsOfInterestMarkers = () => {
    return (
      <MarkerClusterer styles={pointsOfInterestClusterStyle}>
        {placeList?.length &&
          placeList.map(
            place =>
              place.approved && (
                <Marker
                  key={place.id}
                  icon={{
                    url: mechanicalWorkshopIcon.url,
                    scaledSize: new google.maps.Size(mechanicalWorkshopIcon.scaledSize.width, mechanicalWorkshopIcon.scaledSize.height),
                  }}
                  position={{ lat: place.latitude ?? 0, lng: place.longitude ?? 0 }}
                  label={{ text: place.name ?? '', className: 'label-icon' }}
                />
              )
          )}
      </MarkerClusterer>
    );
  };

  function onClickMapType() {
    document.getElementsByClassName('gm-style-mtc')[0].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.MAP);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.MAP);
    });
    document.getElementsByClassName('gm-style-mtc')[1].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.SATELLITE);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.SATELLITE);
    });
  }

  const setFitBounds = () => {
    const gm = window.google?.maps;
    const map = mapRef.current;
    if (!gm || !map) return;

    let listBounds: Array<{ lat?: number | null; lng?: number | null }> = [];

    if (isValidLatLng(focusedAsset)) {
      listBounds = [focusedAsset];
    } else if (vehicles?.length) {
      listBounds = vehicles.filter(isValidLatLng);
    } else {
      listBounds = initialPosition;
    }

    if (listBounds.length === 0) {
      map.setZoom(zoom);
      map.setCenter(center);
      return;
    }

    const boundsLiteral = listBounds.reduce(
      (acc, item) => {
        if (isValidLatLng(item)) {
          const lat = Number(item.lat);
          const lng = Number(item.lng);
          return {
            north: Math.max(acc.north, lat),
            south: Math.min(acc.south, lat),
            east: Math.max(acc.east, lng),
            west: Math.min(acc.west, lng),
          };
        }
        return acc;
      },
      { north: -90, south: 90, east: -180, west: 180 }
    );

    if (boundsLiteral.north >= boundsLiteral.south && boundsLiteral.east >= boundsLiteral.west) {
      map.fitBounds(boundsLiteral);
    }
  };

  // Only show spinner on first load (list is still empty), hide during background refreshes
  const renderLoading = useCallback(
    () =>
      loadingCompanyInfo &&
      vehicles.length === 0 && (
        <div className="spinner-map">
          <Spinner color="#000814" />
        </div>
      ),
    [loadingCompanyInfo, vehicles.length]
  );

  // Update yellow circle at Maintenance Tab
  useEffect(() => {
    dispatch(countMaintenanceControl());
  }, []);

  useEffect(() => {
    canSeeVirtualFences && isHiddenCircle && !virtualFenceCircleList.length && dispatch(getEntitiesVirtualFenceCircle({}));
    canSeeVirtualFences && isHiddenCircle && !virtualFencePolygonList.length && dispatch(getEntitiesVirtualFencePolygon());
  }, [canSeeVirtualFences, isHiddenCircle]);

  useEffect(() => {
    isHiddenPlaces && !placeList.length && dispatch(getEntitiesPlaces({}));
  }, [isHiddenPlaces]);

  // First time data arrives: fit once.
  useEffect(() => {
    if (!hasInitialFitRef.current && vehicles.length > 0) {
      hasInitialFitRef.current = true;
      setFitBounds();
    }
  }, [vehicles]);

  // User selected/searched a vehicle: fit to that.
  useEffect(() => {
    if (focusedAsset != null) {
      setFitBounds();
    }
  }, [focusedAsset, focusedAssetTick]);

  // Re-centers the map on the active marker when the map container is resized.
  useEffect(() => {
    if (activeMarkerId == null) return;
    const item = vehicles.find(v => Number(v.id) === activeMarkerId);
    if (!item || item.lat == null || item.lng == null) return;

    const mapEl = document.querySelector('.gm-style')?.parentElement;
    if (!mapEl || typeof ResizeObserver === 'undefined') return;

    setAvailableMapHeight(mapEl.clientHeight);

    let frameId: number | null = null;
    let lastHeight = mapEl.clientHeight;
    const observer = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect?.height ?? mapEl.clientHeight;
      if (h === lastHeight) return;
      lastHeight = h;
      if (frameId != null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        setAvailableMapHeight(h);
        mapRef.current?.panTo({ lat: item.lat, lng: item.lng });
      });
    });
    observer.observe(mapEl);

    return () => {
      observer.disconnect();
      if (frameId != null) cancelAnimationFrame(frameId);
    };
  }, [onActiveMarker, activeMarkerId, vehicles]);

  useEffect(() => {
    setCompanyImplementsInfoDetails(dashboardCompanyImplementsInfoDetails);
    if (dashboardCompanyImplementsInfoDetails) {
      if (detailsTimeoutRef.current) clearTimeout(detailsTimeoutRef.current);
      setDetailsTimedOut(false);
    }
  }, [dashboardCompanyImplementsInfoDetails]);

  return (
    <Fragment>
      {truckImplementId && renderRedirect()}
      <GoogleMap
        ref={mapRef}
        zoom={zoom}
        center={center}
        defaultOptions={{ styles: mapStyle }}
        onTilesLoaded={renderButtonRoutes}
        onMapTypeIdChanged={onClickMapType}
        onIdle={handleMapIdle}
        mapTypeId={google.maps.MapTypeId[mapType]}
      >
        {checkedTraffic && <TrafficLayer />}
        {renderLoading()}
        {renderButtonRoutes()}
        {isHiddenTruckImplements && renderTruckImplementMarkers()}
        {canSeeVirtualFences && isHiddenCircle && renderVirtualFenceCircle()}
        {canSeeVirtualFences && isHiddenCircle && renderVirtualFencePolygons()}
        {isHiddenPlaces && renderPointsOfInterestMarkers()}
      </GoogleMap>
    </Fragment>
  );
};

const G_API_URL = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&&v=3.exp&libraries=geometry,drawing,places`;

export default compose(
  withProps({
    googleMapURL: G_API_URL,
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `100%` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(CompanyTruckImplementsMap);
